/**
 * Toast notification system — surfaces background-chat events without
 * forcing the user to keep tabs on every conversation.
 *
 * Visual: a fixed dark card (in both light and dark themes — see the note
 * on the card below), intent-colored accent stripe on the left,
 * uppercase intent label + conversation title at the top, a 2-line
 * preview of the actual content, and a "Jump to chat →" CTA that
 * navigates to the conversation when present. Question / approval
 * intents are sticky (the agent is blocked until the user acts).
 *
 * Two APIs are exposed:
 *   - ``push(toast)`` — the rich form used for background-chat events.
 *   - ``add(message, type, duration)`` — legacy single-line form kept
 *     as a shim so simple call sites (compaction notices, generic
 *     errors) don't need to be rewritten.
 */

import { useEffect, useState, useCallback } from "react";
import { create } from "zustand";
import { useChatStore } from "@/stores/chatStore";

/** Look up openConversation lazily — avoids a hard import cycle since
 *  chatStore also imports from this module. ``getState()`` works at
 *  call-time after both modules have evaluated. */
function jumpToConversation(id: string): void {
  void useChatStore.getState().openConversation(id);
}

export type ToastIntent =
  | "response"
  | "shortlist"
  | "question"
  | "approval"
  | "error"
  | "info"
  | "success";

/** Optional leading glyph. "spinner" animates (work in progress); "check" is a
 *  success tick. Used by status toasts (e.g. the CLI auto-update). */
export type ToastGlyph = "spinner" | "check";

/** Optional CTA rendered like "Jump to chat" — runs the callback then
 *  dismisses (e.g. "Continue sync" on the sync-error toast). */
export interface ToastAction {
  label: string;
  run: () => void;
}

interface ToastItem {
  id: string;
  intent: ToastIntent;
  /** Conversation title — rendered next to the intent label. */
  title?: string;
  /** Single-line (visually 2-line, clamped) preview of the content. */
  preview: string;
  /** When set, the toast renders a "Jump to chat →" CTA. */
  conversationId?: string;
  /** Auto-dismiss in ms. 0 / undefined = sticky until user acts. */
  duration?: number;
  /** Optional leading status glyph (spinner / check). */
  icon?: ToastGlyph;
  /** Optional action CTA. */
  action?: ToastAction;
}

interface ToastPushInput {
  intent: ToastIntent;
  title?: string;
  preview: string;
  conversationId?: string;
  duration?: number;
  icon?: ToastGlyph;
  action?: ToastAction;
}

interface ToastState {
  toasts: ToastItem[];
  push: (input: ToastPushInput) => string;
  /** Legacy single-line API kept as a shim for non-chat-event sites. */
  add: (
    message: string,
    type?: "error" | "success" | "info",
    duration?: number,
  ) => string;
  /** Patch an existing toast in place (e.g. live progress on a sticky card). */
  update: (id: string, partial: Partial<ToastPushInput>) => void;
  remove: (id: string) => void;
}

const MAX_VISIBLE_TOASTS = 4;

/** Default auto-dismiss per intent (ms). 0 = sticky. */
const DEFAULT_DURATION: Record<ToastIntent, number> = {
  response: 8000,
  shortlist: 9000,
  // Question / approval block the agent — the user MUST act, so the
  // toast stays put until they click or dismiss it explicitly.
  question: 0,
  approval: 0,
  error: 12000,
  info: 5000,
  success: 5000,
};

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (input) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const item: ToastItem = {
      id,
      intent: input.intent,
      title: input.title,
      preview: input.preview,
      conversationId: input.conversationId,
      duration: input.duration ?? DEFAULT_DURATION[input.intent],
      icon: input.icon,
      action: input.action,
    };
    set((s) => {
      // Cap visible stack — oldest non-sticky toasts drop off so a wave
      // of background updates doesn't bury the user in cards.
      const next = [...s.toasts, item];
      if (next.length > MAX_VISIBLE_TOASTS) {
        const overflow = next.length - MAX_VISIBLE_TOASTS;
        // Prefer to drop auto-dismissing toasts before sticky ones.
        for (let i = 0; i < next.length && next.length - i > MAX_VISIBLE_TOASTS; ) {
          if ((next[i]?.duration ?? 0) > 0) {
            next.splice(i, 1);
          } else {
            i += 1;
          }
        }
        // If still overflowing (all sticky), drop the oldest.
        while (next.length > MAX_VISIBLE_TOASTS) next.shift();
        void overflow;
      }
      return { toasts: next };
    });
    return id;
  },
  add: (message, type = "info", duration) => {
    const intent: ToastIntent = type;
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    set((s) => ({
      toasts: [
        ...s.toasts,
        { id, intent, preview: message, duration: duration ?? DEFAULT_DURATION[intent] },
      ],
    }));
    return id;
  },
  update: (id, partial) => {
    set((s) => ({
      toasts: s.toasts.map((t) => (t.id === id ? { ...t, ...partial } : t)),
    }));
  },
  remove: (id) => {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
}));

/** Intent → accent stripe color class. Kept brand-aligned; no green. */
const STRIPE: Record<ToastIntent, string> = {
  response: "bg-jove",
  shortlist: "bg-jove",
  question: "bg-gold",
  approval: "bg-jove-80",
  error: "bg-hydra",
  info: "bg-space-40",
  success: "bg-jove-60",
};

/** Human-readable label rendered in the small uppercase top row. */
const LABEL: Record<ToastIntent, string> = {
  response: "Response ready",
  shortlist: "Shortlist ready",
  question: "Needs your input",
  approval: "Approval requested",
  error: "Error",
  info: "Notice",
  success: "Done",
};

/** Leading status glyph. Brand-aligned (Jove accent on the dark card). */
function ToastGlyphIcon({ kind }: { kind: ToastGlyph }) {
  if (kind === "spinner") {
    return (
      <svg className="h-5 w-5 animate-spin text-jove-60" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle className="opacity-25" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" />
        <path
          className="opacity-90"
          d="M21 12a9 9 0 0 0-9-9"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  // "check"
  return (
    <svg className="h-5 w-5 text-jove-60" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" className="opacity-30" />
      <path
        d="M7.5 12.5l3 3 6-6.5"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ToastCard({ toast }: { toast: ToastItem }) {
  const remove = useToastStore((s) => s.remove);
  const [exiting, setExiting] = useState(false);

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => remove(toast.id), 200);
  }, [remove, toast.id]);

  const jump = useCallback(() => {
    if (toast.conversationId) {
      jumpToConversation(toast.conversationId);
    }
    dismiss();
  }, [toast.conversationId, dismiss]);

  useEffect(() => {
    if (!toast.duration) return;
    const timer = setTimeout(dismiss, toast.duration);
    return () => clearTimeout(timer);
  }, [toast.duration, dismiss]);

  const runAction = useCallback(() => {
    toast.action?.run();
    dismiss();
  }, [toast.action, dismiss]);

  const stripeClass = STRIPE[toast.intent] ?? STRIPE.info;
  const label = LABEL[toast.intent] ?? LABEL.info;
  const hasJump = !!toast.conversationId;
  const hasAction = !!toast.action;

  // The card is pinned to a FIXED dark surface (``#2B2A27``) in BOTH themes,
  // not the ``bg-space`` token. ``space`` flips to a light cream in dark mode
  // (see index.css), which turned the "dark card" light while the white ink
  // stayed white — an invisible-text contrast bug. A fixed dark toast (à la
  // Sonner/react-hot-toast) reads correctly on the light page and lifts off the
  // warm-dark page via the ``border-white/10`` hairline + shadow. All ink is
  // therefore fixed light-on-dark (``text-white`` / ``text-jove-60``), never a
  // themeable token that would flip against this fixed background.
  //
  // Note: the outer card intentionally does NOT use ``role="button"``. A
  // global reset in ``index.css`` forces ``background-color: transparent``
  // on every ``[role="button"]`` (so PyWebView/GTK doesn't paint native
  // button chrome), which would beat the ``bg-[#2b2a27]`` fill and render
  // the card invisible until a hover re-applies a background utility.
  // Keyboard activation lives on the inner "Jump to chat" button instead —
  // semantically correct, and side-steps the global override entirely.
  return (
    <div
      onClick={hasJump ? jump : undefined}
      className={`flex items-stretch rounded-xl shadow-modal max-w-md overflow-hidden
        bg-[#2b2a27] text-white border border-white/10 transition-all duration-200
        ${exiting ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}
        ${hasJump ? "cursor-pointer" : ""}`}
    >
      <div className={`w-1 shrink-0 ${stripeClass}`} aria-hidden />
      {toast.icon && (
        <div className="flex items-center pl-3 -mr-1 shrink-0">
          <ToastGlyphIcon kind={toast.icon} />
        </div>
      )}
      <div className="flex-1 min-w-0 px-4 py-3 flex flex-col gap-1.5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-white/50 font-medium">
          <span>{label}</span>
          {toast.title && (
            <>
              <span aria-hidden>•</span>
              <span className="truncate text-white/70 normal-case tracking-normal text-xs font-semibold">
                {toast.title}
              </span>
            </>
          )}
        </div>
        <p className="text-sm leading-snug line-clamp-2 text-white/90">{toast.preview}</p>
        {(hasJump || hasAction || toast.duration === 0) && (
          <div className="flex items-center justify-between gap-2 pt-1">
            {hasJump ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  jump();
                }}
                className="text-xs font-medium text-jove-60 hover:text-white transition-colors inline-flex items-center gap-1"
              >
                Jump to chat
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : hasAction ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  runAction();
                }}
                className="text-xs font-medium text-jove-60 hover:text-white transition-colors inline-flex items-center gap-1"
              >
                {toast.action!.label}
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <span />
            )}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          dismiss();
        }}
        className="px-3 text-white/50 hover:text-white transition-colors"
        aria-label="Dismiss notification"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 items-end">
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} />
      ))}
    </div>
  );
}
